import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import useSnackbar from "@/hooks/use-snackbar";
import {
  useGetCompareDefaultCarsConfigQuery,
  useUpdateCompareDefaultCarsConfigMutation,
} from "@/app/features/compareModels/compareModelsApiSlice";
import { revalidateCompareDefaultsOnFrontend } from "@/utils/revalidateCompareDefaults";

const CAR_ID_FIELDS = [
  "compareDefaultCarId1",
  "compareDefaultCarId2",
  "compareDefaultCarId3",
];

/** Shift non-empty car IDs left (empty slots trail). */
function compactCarIds(form) {
  const ids = CAR_ID_FIELDS.map((field) => form[field]?.trim() || "").filter(
    Boolean
  );
  return {
    compareDefaultCarId1: ids[0] || "",
    compareDefaultCarId2: ids[1] || "",
    compareDefaultCarId3: ids[2] || "",
  };
}

const CompareDefaultsConfigPage = () => {
  const [formData, setFormData] = useState({
    compareDefaultCarId1: "",
    compareDefaultCarId2: "",
    compareDefaultCarId3: "",
  });

  const { data, isLoading, error, refetch } = useGetCompareDefaultCarsConfigQuery();
  const [updateConfig, { isLoading: updateLoading }] =
    useUpdateCompareDefaultCarsConfigMutation();
  const { showSnackbar, snackbar } = useSnackbar();

  React.useEffect(() => {
    if (data?.data) {
      setFormData({
        compareDefaultCarId1: data.data.compareDefaultCarId1 || "",
        compareDefaultCarId2: data.data.compareDefaultCarId2 || "",
        compareDefaultCarId3: data.data.compareDefaultCarId3 || "",
      });
    }
  }, [data]);

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const compacted = compactCarIds(formData);

    try {
      const result = await updateConfig(compacted).unwrap();

      if (result.success) {
        setFormData(result.data ?? compacted);

        try {
          await revalidateCompareDefaultsOnFrontend();
        } catch (revalidateError) {
          console.warn("Compare defaults saved; frontend cache refresh failed:", revalidateError);
        }

        showSnackbar({
          message: "Compare Cars defaults updated successfully",
          type: "success",
        });
        refetch();
      } else {
        showSnackbar({
          message: result.message || "Failed to update Compare Cars defaults",
          type: "error",
        });
      }
    } catch {
      showSnackbar({
        message: "An error occurred while updating Compare Cars defaults",
        type: "error",
      });
    }
  };

  const resolvedSlots = data?.data?.slots || [];

  const slotLabel = (index) => {
    const field = CAR_ID_FIELDS[index];
    const carId = formData[field]?.trim();

    if (!carId) {
      return "Leave empty for a blank compare column";
    }

    const slot = resolvedSlots[index];
    const slotCarId = slot?.carId?.trim() || "";

    if (slotCarId && slotCarId.toLowerCase() === carId.toLowerCase() && slot?.make) {
      return `${slot.make} ${slot.model}${slot.grade ? ` · ${slot.grade}` : ""}`;
    }

    if (slotCarId && slotCarId.toLowerCase() === carId.toLowerCase()) {
      return "Not resolved — check car ID";
    }

    const savedCarId = (data?.data?.[field] || "").trim();
    if (carId !== savedCarId) {
      return "Will resolve after save";
    }

    return "Not resolved — check car ID";
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load Compare Cars default configuration
      </Alert>
    );
  }

  return (
    <>
      {snackbar}
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Compare Cars — Default Selection
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Set up to three vehicle car IDs to pre-load on the Compare Cars page when a user opens it
          from the main menu (no vehicles selected in the URL yet). Use the same car ID format as
          Hero Model (e.g. make_model_variant).
        </Typography>

        <Card>
          <CardContent>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Default car 1 — Car ID"
                    value={formData.compareDefaultCarId1}
                    onChange={handleInputChange("compareDefaultCarId1")}
                    placeholder="e.g. toyota_camry_ascend"
                    helperText={slotLabel(0)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Default car 2 — Car ID"
                    value={formData.compareDefaultCarId2}
                    onChange={handleInputChange("compareDefaultCarId2")}
                    placeholder="Car ID (optional)"
                    helperText={slotLabel(1)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Default car 3 — Car ID"
                    value={formData.compareDefaultCarId3}
                    onChange={handleInputChange("compareDefaultCarId3")}
                    placeholder="Car ID (optional)"
                    helperText={slotLabel(2)}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={updateLoading}
                  startIcon={<CompareArrowsIcon />}
                  sx={{ minWidth: 120 }}
                >
                  {updateLoading ? <CircularProgress size={20} /> : "Save Configuration"}
                </Button>
                <Button variant="outlined" onClick={() => refetch()} disabled={updateLoading}>
                  Refresh
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            How it works
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            • When a visitor opens Compare Cars from the site menu with no vehicles in the URL, these
            defaults are applied automatically.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            • If the URL already includes make/model/grade parameters, those take precedence (shared
            links are unchanged).
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Leave a slot empty to show a blank compare column for that position.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • On save, filled slots are shifted left (e.g. car 3 moves to car 2 if car 2 is
            empty).
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default CompareDefaultsConfigPage;
